import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  FileCode,
  AlertCircle,
  Ban,
  TrendingUp,
  Activity
} from 'lucide-react';
import { dashboardApi } from '../../services/iso20022Api';
import {
  formatCurrency,
  formatDate,
  getStatusBadgeClass,
  getStatusText,
  getDirectionBadgeClass,
  copyToClipboard,
  downloadFile
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const TransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [statusXmlModalOpen, setStatusXmlModalOpen] = useState(false);
  const [selectedStatusXml, setSelectedStatusXml] = useState(null);

  useEffect(() => {
    loadTransaction();
  }, [id]);

  const loadTransaction = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getTransactionById(id);
      setTransaction(data);
    } catch (err) {
      console.error('Failed to load transaction:', err);
      setError('Transaction not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (field, value) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDownloadXml = () => {
    // Prefer rawXml over xmlContent for accuracy
    const xmlToDownload = transaction?.rawXml || transaction?.xmlContent;
    if (xmlToDownload) {
      const fileName = `transaction-${transaction.id}-${transaction.messageType || 'message'}.xml`;
      downloadFile(xmlToDownload, fileName, 'application/xml');
    }
  };

  const handleViewXml = () => {
    setXmlModalOpen(true);
  };

  const handleViewStatusXml = (status) => {
    if (status.xmlContent) {
      setSelectedStatusXml({
        xml: status.xmlContent,
        fileName: `status-${status.id}-${status.txSts}.xml`,
        title: `Status Message #${status.id} - ${status.statusLabel || status.txSts}`
      });
      setStatusXmlModalOpen(true);
    }
  };

  // Helper function to get icon for status
  const getStatusIcon = (statusCategory) => {
    switch (statusCategory) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-danger-600" />;
      case 'cancelled':
        return <Ban className="w-5 h-5 text-danger-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-warning-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  // Helper function to get badge color for status category
  const getCategoryBadgeClass = (statusCategory) => {
    switch (statusCategory) {
      case 'success':
        return 'badge-success';
      case 'rejected':
      case 'cancelled':
        return 'badge-danger';
      case 'pending':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading transaction details...</p>
        </div>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Transaction Not Found</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/iso20022/transactions')} className="btn-primary">
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  const InfoRow = ({ label, value, copyable = false, fieldName = '' }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-center justify-between">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 font-mono flex items-center space-x-2">
          <span>{value || 'N/A'}</span>
          {copyable && value && (
            <button
              onClick={() => handleCopy(fieldName, value)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy to clipboard"
            >
              {copiedField === fieldName ? (
                <CheckCircle className="w-4 h-4 text-success-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}
        </dd>
      </div>
    </div>
  );

  // Determine which XML content to use (prefer rawXml)
  const xmlContent = transaction?.rawXml || transaction?.xmlContent;
  const hasXmlContent = Boolean(xmlContent);

  // Check if there's status history
  const hasStatusHistory = transaction?.statusHistory && transaction.statusHistory.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/transactions')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Transaction #{transaction.id}
            </h1>
            <p className="text-gray-600 mt-1">
              Created {formatDate(transaction.createdAt, 'PPpp')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {hasXmlContent && (
            <>
              <button
                onClick={handleViewXml}
                className="btn-secondary flex items-center space-x-2"
                title="View XML in modal"
              >
                <FileCode className="w-4 h-4" />
                <span>View XML</span>
              </button>
              <button
                onClick={handleDownloadXml}
                className="btn-secondary flex items-center space-x-2"
                title="Download XML file"
              >
                <Download className="w-4 h-4" />
                <span>Download XML</span>
              </button>
            </>
          )}
          <button
            onClick={loadTransaction}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Current Status Banner */}
      <div className={`rounded-lg p-6 ${
        transaction.status === 'ACCP' || transaction.status === 'ACSC' || transaction.status === 'ACSP'
          ? 'bg-success-50 border border-success-200'
          : transaction.status === 'RJCT' || transaction.status === 'CANC'
          ? 'bg-danger-50 border border-danger-200'
          : 'bg-warning-50 border border-warning-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {transaction.status === 'ACCP' || transaction.status === 'ACSC' || transaction.status === 'ACSP' ? (
              <CheckCircle className="w-8 h-8 text-success-600" />
            ) : transaction.status === 'RJCT' || transaction.status === 'CANC' ? (
              <XCircle className="w-8 h-8 text-danger-600" />
            ) : (
              <Clock className="w-8 h-8 text-warning-600" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-600">Current Status</p>
              <p className="text-2xl font-bold text-gray-900">
                {getStatusText(transaction.status)}
              </p>
              {transaction.statusReason && (
                <p className="text-sm text-gray-700 mt-1">
                  {transaction.statusReason}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-600">Direction</p>
            <span className={`badge text-lg ${getDirectionBadgeClass(transaction.direction)}`}>
              {transaction.direction}
            </span>
          </div>
        </div>
      </div>

      {/* Status History Timeline - NEW SECTION */}
      {hasStatusHistory && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Status History</h2>
            </div>
            <span className="text-sm text-gray-600">
              {transaction.statusHistory.length} {transaction.statusHistory.length === 1 ? 'update' : 'updates'}
            </span>
          </div>
          <div className="card-body">
            <div className="flow-root">
              <ul className="-mb-8">
                {transaction.statusHistory.map((status, index) => (
                  <li key={status.id}>
                    <div className="relative pb-8">
                      {/* Vertical line connecting status items */}
                      {index !== transaction.statusHistory.length - 1 && (
                        <span
                          className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      )}
                      
                      <div className="relative flex items-start space-x-3">
                        {/* Icon */}
                        <div className="relative">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white ${
                            status.statusCategory === 'success' ? 'bg-success-100' :
                            status.statusCategory === 'rejected' ? 'bg-danger-100' :
                            status.statusCategory === 'cancelled' ? 'bg-danger-100' :
                            status.statusCategory === 'pending' ? 'bg-warning-100' :
                            'bg-gray-100'
                          }`}>
                            {getStatusIcon(status.statusCategory)}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className={`badge ${getCategoryBadgeClass(status.statusCategory)}`}>
                                {status.txSts}
                              </span>
                              {status.statusLabel && (
                                <span className="text-sm font-medium text-gray-900">
                                  {status.statusLabel}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                              {formatDate(status.timestamp, 'PPp')}
                            </span>
                          </div>

                          {/* Additional details */}
                          <div className="mt-2 text-sm text-gray-600 space-y-1">
                            {status.grpSts && (
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-500">Group Status:</span>
                                <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                                  {status.grpSts}
                                </span>
                              </div>
                            )}
                            
                            {(status.reasonCode || status.reasonText) && (
                              <div className="flex items-start space-x-2">
                                <span className="text-gray-500">Reason:</span>
                                <span className="flex-1">
                                  {status.reasonCode && (
                                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded mr-2">
                                      {status.reasonCode}
                                    </span>
                                  )}
                                  {status.reasonText}
                                </span>
                              </div>
                            )}

                            <div className="flex items-center space-x-4 text-xs">
                              {status.direction && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-gray-500">Direction:</span>
                                  <span className={`badge badge-sm ${getDirectionBadgeClass(status.direction)}`}>
                                    {status.direction}
                                  </span>
                                </div>
                              )}
                              
                              {status.signatureOk !== null && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-gray-500">Signature:</span>
                                  <span className={`flex items-center ${status.signatureOk ? 'text-success-600' : 'text-danger-600'}`}>
                                    {status.signatureOk ? (
                                      <>
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Valid
                                      </>
                                    ) : (
                                      <>
                                        <XCircle className="w-3 h-3 mr-1" />
                                        Invalid
                                      </>
                                    )}
                                  </span>
                                </div>
                              )}

                              {status.stsMsgId && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-gray-500">Msg ID:</span>
                                  <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                                    {status.stsMsgId}
                                  </span>
                                </div>
                              )}

                              {/* View XML Button */}
                              {status.xmlContent && (
                                <button
                                  onClick={() => handleViewStatusXml(status)}
                                  className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 transition-colors"
                                  title="View XML message"
                                >
                                  <FileCode className="w-3 h-3" />
                                  <span>View XML</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* No Status History Message */}
      {!hasStatusHistory && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Status History</h2>
          </div>
          <div className="card-body">
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No status updates available yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Status updates will appear here once pacs.002 messages are received
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Transaction Information</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow
                label="Message Type"
                value={transaction.messageType || transaction.msgType || 'pacs.008'}
              />
              <InfoRow
                label="End-to-End ID"
                value={transaction.endToEndId}
                copyable
                fieldName="endToEndId"
              />
              <InfoRow
                label="Transaction ID"
                value={transaction.txId}
                copyable
                fieldName="txId"
              />
              <InfoRow
                label="Group Message ID"
                value={transaction.grpMsgId}
                copyable
                fieldName="grpMsgId"
              />
              <InfoRow
                label="Amount & Currency"
                value={transaction.amountCcy || 
                      (transaction.amount && transaction.currency 
                        ? `${transaction.amount} ${transaction.currency}` 
                        : 'N/A')}
              />
              <InfoRow label="Settlement Method" value={transaction.settlementMethod} />
            </dl>
          </div>
        </div>

        {/* Party Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Party Information</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow label="Debtor Name" value={transaction.debtorName} />
              <InfoRow
                label="Debtor Account"
                value={transaction.debtorAccount}
                copyable
                fieldName="debtorAccount"
              />
              <InfoRow label="Creditor Name" value={transaction.creditorName} />
              <InfoRow
                label="Creditor Account"
                value={transaction.creditorAccount}
                copyable
                fieldName="creditorAccount"
              />
            </dl>
          </div>
        </div>

        {/* Technical Details */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Technical Details</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow 
                label="Signature Valid" 
                value={transaction.signatureOk ? 'Yes' : 'No'} 
              />
              <InfoRow 
                label="Duplicate Check Hash" 
                value={transaction.dedupHash} 
                copyable 
                fieldName="dedupHash" 
              />
              <InfoRow label="Created At" value={formatDate(transaction.createdAt, 'PPpp')} />
              <InfoRow label="Updated At" value={formatDate(transaction.updatedAt, 'PPpp')} />
            </dl>
          </div>
        </div>

        {/* Status Details */}
        {(transaction.txSts || transaction.grpSts) && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Status Details</h2>
            </div>
            <div className="card-body">
              <dl>
                {transaction.txSts && (
                  <InfoRow label="Transaction Status" value={transaction.txSts} />
                )}
                {transaction.grpSts && (
                  <InfoRow label="Group Status" value={transaction.grpSts} />
                )}
                {transaction.statusReason && (
                  <InfoRow label="Status Reason" value={transaction.statusReason} />
                )}
              </dl>
            </div>
          </div>
        )}
      </div>

      {/* XML Content Preview Card */}
      {hasXmlContent && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">XML Preview</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleViewXml}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
              >
                <FileCode className="w-4 h-4" />
                <span>View Full XML</span>
              </button>
              <button
                onClick={() => handleCopy('xml', xmlContent)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
              >
                {copiedField === 'xml' ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy XML</span>
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="card-body">
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-64">
              {xmlContent.split('\n').slice(0, 20).join('\n')}
              {xmlContent.split('\n').length > 20 && '\n...\n(Click "View Full XML" to see complete content)'}
            </pre>
          </div>
        </div>
      )}

      {/* XML Viewer Modal for Transaction */}
      <XmlViewerModal
        isOpen={xmlModalOpen}
        onClose={() => setXmlModalOpen(false)}
        xmlContent={xmlContent}
        fileName={`transaction-${transaction.id}-${transaction.messageType || 'message'}.xml`}
        title={`XML Message - Transaction #${transaction.id}`}
      />

      {/* XML Viewer Modal for Status Messages */}
      {selectedStatusXml && (
        <XmlViewerModal
          isOpen={statusXmlModalOpen}
          onClose={() => {
            setStatusXmlModalOpen(false);
            setSelectedStatusXml(null);
          }}
          xmlContent={selectedStatusXml.xml}
          fileName={selectedStatusXml.fileName}
          title={selectedStatusXml.title}
        />
      )}
    </div>
  );
};

export default TransactionDetail;
